import { Theme } from '@material-ui/core'
import { withStyles, WithStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import { Box } from '@mui/material'
import { ParentSize } from '@visx/responsive'
import sum from 'lodash/sum'
import React, { ReactNode, useEffect, useMemo } from 'react'
import 'react-virtualized/styles.css'
import { Index, TableRowProps } from 'react-virtualized'

import { TABLE_ROW_HEIGHT, VirtualizedTable } from 'src/components/Table'

import { IColumnProps } from '../VirtualizedTable'

const STYLE_NOTICE_DIMENSIONS = {
  height: 30,
  marginBottom: 8,
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    content: {
      width: '100%',
      height: '100%',
      minHeight: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      overflow: 'scroll',
    },
    noticeCard: {
      display: 'flex',
      ...STYLE_NOTICE_DIMENSIONS,
    },
    noticeContent: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px',
      '&:last-child': {
        paddingBottom: '8px',
      },
    },
    noticeImg: {
      color: theme.palette.primary.main,
      marginRight: theme.spacing(),
    },
  })

/* Template for a autosized, virtualized table with an export button */

interface IProps {
  className?: string
  columns: IColumnProps[]
  data: any
  dataTestId?: string
  endRowRenderer?: (rowRendererProps: TableRowProps) => ReactNode
  endRowRendererWidth?: number
  rowHeight?: number | ((index: Index) => number)
  rowRenderer?: (rowRendererProps: TableRowProps) => JSX.Element
  setIsOverDimension: (status: boolean) => void
  totalCount?: number
  truncated?: boolean
}

const ExportableTable = (props: WithStyles<typeof styles> & IProps) => {
  const { classes, data, columns, totalCount, setIsOverDimension } = props
  const computedWidth = useMemo(
    () =>
      sum(columns.map((c) => c.width)) +
      (props.endRowRendererWidth ? props.endRowRendererWidth + 10 * (columns.length + 1) : 0),
    [columns, props.endRowRendererWidth]
  )

  useEffect(() => {
    setIsOverDimension(props.truncated ?? props.data.length < (totalCount ?? 0))
  }, [props.truncated, props.data.length, totalCount])

  return (
    <ParentSize className={classes.root}>
      {({ height, width }: any) => (
        <Box sx={{ width }}>
          <VirtualizedTable
            items={data}
            columnProps={columns}
            height={Math.max(100, height)}
            width={Math.max(computedWidth, width)}
            rowHeight={props.rowHeight ?? TABLE_ROW_HEIGHT}
            headerHeight={TABLE_ROW_HEIGHT * 1.3}
            rowRenderer={props.rowRenderer}
            endRowRenderer={props.endRowRenderer}
            endRowRendererWidth={props.endRowRendererWidth}
            dataTestId={props.dataTestId}
          />
        </Box>
      )}
    </ParentSize>
  )
}

export default withStyles(styles)(ExportableTable)
